import { Link } from 'react-router-dom';
import { Instagram, Twitter, Linkedin, Facebook } from 'lucide-react';
import { useCountry } from '../context/CountryContext';
import { useTranslation } from 'react-i18next';

const Footer = () => {
    const { activeCountry } = useCountry();
    const currentYear = new Date().getFullYear();
    const { t } = useTranslation();

    const getPath = (path: string) => {
        if (path.startsWith('http') || path.startsWith('/admin')) return path;
        const prefix = activeCountry ? `/${activeCountry.code.toLowerCase()}` : '';
        if (path === '/') return prefix || '/';
        return `${prefix}${path.startsWith('/') ? path : '/' + path}`;
    };

    const linkStyle = "text-navy-700 dark:text-gray-300 text-sm hover:underline transition-all duration-micro ease-brand font-normal";
    const headerStyle = "text-navy dark:text-white font-bold text-sm mb-4";

    return (
        <footer className="bg-ui dark:bg-zinc-900 border-t border-transparent py-12 font-sans">
            <div className="w-full max-w-[1460px] 2xl:max-w-[1520px] mx-auto px-6 md:px-12 lg:px-20 xl:px-24 2xl:px-20">
                
                {/* Main Footer Grid */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-10 mb-10">
                    
                    {/* Column 1: Platform */}
                    <div>
                        <h4 className={headerStyle}>{t('footer_platform')}</h4>
                        <ul className="space-y-3">
                            <li><Link to={getPath('/search?type=properties')} className={linkStyle}>{t('footer_properties')}</Link></li>
                            <li><Link to={getPath('/search?type=compounds')} className={linkStyle}>{t('footer_compounds')}</Link></li>
                            <li><Link to={getPath('/launches')} className={linkStyle}>{t('footer_launches')}</Link></li>
                            <li><Link to={getPath('/ai-match')} className={linkStyle}>{t('footer_ai_match')}</Link></li>
                        </ul>
                    </div>

                    {/* Column 2: Company */}
                    <div>
                        <h4 className={headerStyle}>{t('footer_company')}</h4>
                        <ul className="space-y-3">
                            <li><Link to={getPath('/about')} className={linkStyle}>{t('footer_about')}</Link></li>
                            <li><Link to={getPath('/contact')} className={linkStyle}>{t('footer_contact')}</Link></li>
                            <li><Link to={getPath('/careers')} className={linkStyle}>{t('footer_careers')}</Link></li>
                            <li><a href="mailto:concierge@laqy.com" className={linkStyle}>concierge@laqy.com</a></li>
                            <li>
                                <a href="https://partners.laqy.com" target="_blank" rel="noopener noreferrer" className={linkStyle}>
                                    {t('footer_verify_agent')}
                                </a>
                            </li>
                            <li>
                                <Link to={getPath('/partners/login')} className={linkStyle}>
                                    {t('footer_partner_login')}
                                </Link>
                            </li>
                        </ul>
                    </div>

                    {/* Column 3: Legal & Social */}
                    <div>
                        <h4 className={headerStyle}>{t('footer_connect')}</h4>
                        <ul className="space-y-3 mb-6">
                            <li><Link to={getPath('/privacy')} className={linkStyle}>{t('footer_privacy')}</Link></li>
                            <li><Link to={getPath('/terms')} className={linkStyle}>{t('footer_terms')}</Link></li>
                        </ul>
                        <div className="flex items-center gap-3">
                            <a href="#" className="text-navy dark:text-gray-400 hover:text-terracotta dark:hover:text-terracotta-400 transition-colors duration-micro ease-brand">
                                <Instagram size={20} strokeWidth={2} />
                            </a>
                            <a href="#" className="text-navy dark:text-gray-400 hover:text-terracotta dark:hover:text-terracotta-400 transition-colors duration-micro ease-brand">
                                <Twitter size={20} strokeWidth={2} />
                            </a>
                            <a href="#" className="text-navy dark:text-gray-400 hover:text-terracotta dark:hover:text-terracotta-400 transition-colors duration-micro ease-brand">
                                <Linkedin size={20} strokeWidth={2} />
                            </a>
                            <a href="#" className="text-navy dark:text-gray-400 hover:text-terracotta dark:hover:text-terracotta-400 transition-colors duration-micro ease-brand">
                                <Facebook size={20} strokeWidth={2} />
                            </a>
                        </div>
                    </div>
                </div>

                {/* Branding Logo */}
                <div className="mb-8 pt-2 flex items-center gap-2.5 opacity-90">
                    <img src="/laqysymbolandlogo_premium.svg" alt="Laqy Estate" className="h-10 md:h-12 w-auto" />
                </div>

                {/* Bottom Bar */}
                <div className="pt-8 border-t border-ui-300 dark:border-white/10 flex flex-col md:flex-row justify-between items-center gap-4">
                    <div className="text-sm text-navy-700 dark:text-gray-400">
                        {t('footer_rights', { year: currentYear })}
                    </div>
                    <div className="text-sm text-navy-700 dark:text-gray-400">
                        {t('footer_operational')}
                    </div>
                </div>
            </div>
        </footer>
    );
};

export default Footer;
